import { useChangeCache } from "@/hooks/use-change-cache";
import { useCameraInCurrentScene } from "@/modes/alignment-modes-commons/align-to-cad-utils";
import { EntityPinControls } from "@/registration-tools/common/interaction/entity-pin-controls";
import { InteractionMode } from "@/registration-tools/common/interaction/single-pin-interaction";
import { Perspective } from "@/registration-tools/common/store/registration-datatypes";
import {
  centerCameraOnBoundingBox,
  isTopDownView,
  PERSPECTIVE_DIRECTIONS,
} from "@/registration-tools/utils/camera-views";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { ToolName } from "@/store/ui/ui-slice";
import { CameraViewType } from "@/utils/cam-view";
import {
  computeCachedWorldTransform,
  ExplorationControls,
  ExplorationControlsProps,
  useNonExhaustiveEffect,
  useReproportionCamera,
  useToast,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { EMPTY_ARRAY, GUID, TypedEvent } from "@faro-lotv/foundation";
import {
  Map2DControls as Map2DControlsImpl,
  MovementType,
  SupportedCamera,
  WalkOrbitControls,
} from "@faro-lotv/lotv";
import { useFrame } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  Box3,
  Matrix4,
  MOUSE,
  OrthographicCamera,
  PerspectiveCamera,
  Vector3,
} from "three";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { useCameraAnimation } from "../hooks/use-camera-animation";
import { useLiveDraftTransitionStore } from "../live-draft-transition-store";
import {
  selectAreSelectedScansVisible,
  selectEntitiesToEdit,
  selectHoveredConnectionId,
  selectRevisionEntityWorldPositions,
  selectRevisionEntityWorldTransformCache,
  selectSelectedEntityIdsRecursive,
  selectVisibilityMap,
} from "../store/data-preparation-ui/data-preparation-ui-selectors";
import { selectRevisionEntity } from "../store/revision/revision-entity-selectors";
import {
  computeLocalEntityPoseFromWorldTransforms,
  EntityTransformOverrides,
  RevisionTransformCache,
} from "../store/revision/revision-transform-cache";
import { selectDataPreparationTransformCache } from "../store/user-edits/data-preparation-user-edit-selectors";
import {
  commitInProgressTransformEdit,
  updateInProgressTransformEdit,
} from "../store/user-edits/data-preparation-user-edit-slice";
import { Projection } from "../ui/projection-switch";
import {
  frameOrthoCameraFromPerspectiveView,
  framePerspectiveCameraFromOrthoView,
} from "../utils/ortho-perspective-switch-utils";
import { invalidateSceneCheapEvent } from "./revision-rendering-pipeline";

/**
 * Adapt the mouse bindings to work better for the registration view.
 * The horizontal movement doesn't make much sense in this context.
 */
const MOUSE_BINDINGS_3D: ExplorationControlsProps["mouseBindings"] = {
  [MOUSE.LEFT]: MovementType.Rotating,
  // Instead of horizontal movement
  [MOUSE.RIGHT]: MovementType.CameraPan,
  [MOUSE.MIDDLE]: MovementType.CameraPan,
};

export type CenterCameraProps = {
  /** The camera to center. */
  camera?: SupportedCamera;

  /**
   * The bounding box to center the camera on.
   * Defaults to the bounding box of all scans in the scene.
   */
  bbox?: Box3;

  /**
   * The new perspective to use for the camera.
   * Defaults to the current camera perspective.
   */
  perspective?: Perspective;

  /** Optional world-space point that should stay at the center. */
  referencePoint?: Vector3;
};

type RevisionScansControlsProps = {
  /** An event which is triggered when the camera should be centered. */
  centerCameraEvent: TypedEvent<CenterCameraProps>;

  /** The IDs of the scans in the scene. */
  revisionScanIds: GUID[];

  /** The current projection */
  projection: Projection;

  /** The current camera in 3D projection */
  cameraOptions3D: CameraViewType;

  /** Whether the user can move and rotate scans. */
  isEditingScans: boolean;
};

/**
 * @returns Controls to inspect the scans and manage the camera.
 */
export function RevisionScansControls({
  projection,
  cameraOptions3D,
  revisionScanIds,
  centerCameraEvent,
  isEditingScans,
}: RevisionScansControlsProps): JSX.Element | null {
  const {
    setCamera: setInitialCamera,
    setOrbitControls: setInitialOrbitControls,
    setFromDraftSettings,
    ...initialState
  } = useLiveDraftTransitionStore();

  const [activeProjection, setActiveProjection] = useState(projection);
  const { perspective, activeTool } = useDataPreparationOverlayElements();

  const { openToast } = useToast();
  const { getState, dispatch } = useAppStore();

  // Whether the Draft view inherited a valid top-down 2D camera from the Live view.
  // If it did not, the 2D orthographic camera must be centered on the top view on mount, so
  // that the actual camera matches the perspective switch (which always starts on "Top view").
  const [didInheritValidTopDown2DCamera] = useState(
    () =>
      initialState.projection === Projection.twoDimensional &&
      initialState.camera instanceof OrthographicCamera &&
      isTopDownView(initialState.camera),
  );

  // Using manual=true to prevent threeJs / R3F from interfering with the camera
  const [orthoCamera2D] = useState(() => {
    const camera =
      initialState.projection === Projection.twoDimensional &&
      !!initialState.camera &&
      initialState.camera instanceof OrthographicCamera &&
      isTopDownView(initialState.camera)
        ? initialState.camera.clone()
        : new OrthographicCamera();
    Object.assign(camera, { manual: true, name: "orthographic 2D camera" });
    return camera;
  });
  const [orthoCamera3D] = useState(() => {
    const camera =
      initialState.projection === Projection.threeDimensional &&
      !!initialState.camera &&
      initialState.camera instanceof OrthographicCamera
        ? initialState.camera.clone()
        : new OrthographicCamera();
    Object.assign(camera, { manual: true, name: "orthographic 3D camera" });
    return camera;
  });
  const [perspCamera] = useState(() => {
    const camera =
      initialState.projection === Projection.threeDimensional &&
      !!initialState.camera &&
      initialState.camera instanceof PerspectiveCamera
        ? initialState.camera.clone()
        : new PerspectiveCamera();
    Object.assign(camera, { manual: true, name: "perspective 3D camera" });
    return camera;
  });

  const [active3DCamera, setActive3DCamera] =
    useState<CameraViewType>(cameraOptions3D);

  const explorationControlsRef = useRef<WalkOrbitControls>(null);
  const map2dControlsRef = useRef<Map2DControlsImpl>(null);

  const visibilityMap = useAppSelector(selectVisibilityMap);
  const visibleScanIds = useMemo(
    () => revisionScanIds.filter((scanId) => visibilityMap[scanId] !== false),
    [revisionScanIds, visibilityMap],
  );

  const scanPositions = useAppSelector(
    selectRevisionEntityWorldPositions(visibleScanIds),
  );

  const isAnyConnectionHovered = useAppSelector(selectHoveredConnectionId);

  const combinedScansBbox = useMemo(() => {
    // With no scan positions, setFromPoints leaves the box empty (infinite bounds),
    // which would produce a non-finite camera when framing. Use a unit box around the origin.
    const bbox =
      scanPositions.length === 0
        ? new Box3().setFromCenterAndSize(new Vector3(), new Vector3(1, 1, 1))
        : new Box3().setFromPoints(scanPositions);
    // Add a padding around the bounding box.
    // Ensures that all important scan features are visible.
    // Also fixes camera positioning bugs when the bounding box has 0 volume.
    return bbox.expandByScalar(3);
  }, [scanPositions]);
  const combinedScansCenter = useMemo(
    () => combinedScansBbox.getCenter(new Vector3()),
    [combinedScansBbox],
  );

  const [lastTarget, setLastTarget] = useState<Vector3 | undefined>(
    initialState.camera
      ? initialState.orbitControls?.target
      : combinedScansCenter,
  );

  const { animationToRender, startAnimation, animationCamera } =
    useCameraAnimation();

  let activeCamera: SupportedCamera = orthoCamera2D;

  if (animationToRender) {
    activeCamera = animationCamera;
  } else if (activeProjection === Projection.twoDimensional) {
    activeCamera = orthoCamera2D;
  } else if (active3DCamera === CameraViewType.orthographic) {
    activeCamera = orthoCamera3D;
  } else {
    activeCamera = perspCamera;
  }

  useCameraInCurrentScene(activeCamera);
  useReproportionCamera(activeCamera);

  // Should not re-render for every change in camera position or rotation
  useNonExhaustiveEffect(() => {
    const referencePoint = explorationControlsRef.current?.target;

    // Transition of projection
    if (projection !== activeProjection) {
      // Change from 2D to 3D
      if (projection === Projection.threeDimensional) {
        // Set 3D orthographic camera with respect to 2D Orthographic camera
        orthoCamera3D.position.copy(orthoCamera2D.position);
        orthoCamera3D.quaternion.copy(orthoCamera2D.quaternion);

        // Set 3D perspective camera with respect to 2D Orthographic camera
        setLastTarget(
          framePerspectiveCameraFromOrthoView(
            perspCamera,
            orthoCamera2D,
            lastTarget ?? combinedScansCenter,
          ),
        );
      }
      // 3D Perspective to 2D
      else if (active3DCamera === CameraViewType.perspective) {
        // Re-center camera to force valid positions of 2d camera
        centerCamera({
          camera: orthoCamera2D,
          perspective,
          referencePoint,
        });

        startAnimation(perspCamera, {
          position: orthoCamera2D.position,
          quaternion: orthoCamera2D.quaternion,
          duration: 0.5,
        });

        // Skip logic if controls have been in walk mode. Use the last camera state as a fallback.
        if (referencePoint) {
          setLastTarget(referencePoint);
        }
      }
      // 3D Orthographic to 2D
      else {
        // Re-center camera to force valid positions of 2d camera
        centerCamera({
          camera: orthoCamera2D,
          perspective,
          referencePoint,
        });
      }
      setActiveProjection(projection);
    }
  }, [
    projection,
    activeProjection,
    orthoCamera2D,
    active3DCamera,
    perspCamera,
    lastTarget,
    startAnimation,
  ]);

  useEffect(() => {
    // Transition of 3D camera
    if (cameraOptions3D !== active3DCamera) {
      // 3D Orthographic to 3D Perspective
      if (cameraOptions3D === CameraViewType.perspective) {
        setLastTarget(
          framePerspectiveCameraFromOrthoView(
            perspCamera,
            orthoCamera3D,
            lastTarget ?? combinedScansCenter,
          ),
        );
      }
      // 3D Perspective to 3D Orthographic
      else {
        const referencePoint = explorationControlsRef.current?.target;
        if (referencePoint) {
          frameOrthoCameraFromPerspectiveView(
            orthoCamera3D,
            perspCamera,
            referencePoint,
          );
          setLastTarget(referencePoint);
        }

        orthoCamera3D.position.copy(perspCamera.position);
        orthoCamera3D.quaternion.copy(perspCamera.quaternion);
      }
      setActive3DCamera(cameraOptions3D);
    }
  }, [
    cameraOptions3D,
    active3DCamera,
    perspCamera,
    orthoCamera3D,
    lastTarget,
    combinedScansCenter,
  ]);

  const centerCamera = useCallback(
    ({
      bbox = combinedScansBbox,
      perspective,
      camera: customCamera,
      referencePoint,
    }: CenterCameraProps) => {
      // Determine the camera to use for the animation
      let camera: SupportedCamera;
      if (customCamera) {
        camera = customCamera;
      } else if (activeProjection === Projection.twoDimensional) {
        camera = orthoCamera2D;
      } else if (active3DCamera === CameraViewType.orthographic) {
        camera = orthoCamera3D;
      } else {
        camera = perspCamera;
      }
      const camDirection = perspective
        ? PERSPECTIVE_DIRECTIONS[perspective]
        : undefined;

      const centerBbox = bbox.getCenter(new Vector3());
      const bboxToFrame = referencePoint
        ? bbox.clone().translate(referencePoint.clone().sub(centerBbox))
        : bbox;

      // Disable controls to not interfere with the camera animation
      if (map2dControlsRef.current) {
        map2dControlsRef.current.enabled = false;
      }
      if (explorationControlsRef.current) {
        explorationControlsRef.current.enabled = false;
      }

      centerCameraOnBoundingBox({
        camera,
        bbox: bboxToFrame,
        camDirection,
        startAnimation,
        onAnimationFinished: (target) => {
          // Re-enable controls and sync camera state
          if (map2dControlsRef.current) {
            map2dControlsRef.current.enabled = true;
            map2dControlsRef.current.camera = camera;
          }

          if (explorationControlsRef.current) {
            explorationControlsRef.current.target = target;
            explorationControlsRef.current.enabled = true;
            explorationControlsRef.current.camera = camera;
          }

          setLastTarget(target);
        },
      });
    },
    [
      activeProjection,
      active3DCamera,
      orthoCamera2D,
      orthoCamera3D,
      perspCamera,
      combinedScansBbox,
      startAnimation,
      map2dControlsRef,
      explorationControlsRef,
    ],
  );
  useTypedEvent<CenterCameraProps>(centerCameraEvent, centerCamera);
  // Center once on mount, in case that the entities are loaded before the controls.
  // In 2D, only skip centering when a valid top-down camera was inherited from the Live view;
  // otherwise the camera would show a side view while the perspective switch says "Top view".
  useNonExhaustiveEffect(() => {
    const shouldCenterOnTopView =
      activeProjection === Projection.twoDimensional
        ? !didInheritValidTopDown2DCamera
        : !initialState.camera;
    if (shouldCenterOnTopView) {
      centerCamera({ perspective: Perspective.topView });
    }
  }, []);

  const entitiesToEdit = useAppSelector(selectEntitiesToEdit);
  // All entities that are affected by the edits
  const affectedEntityIds = useAppSelector(selectSelectedEntityIdsRecursive);

  // State to track through the edit
  const interactionMode = useRef<InteractionMode>(InteractionMode.translation);
  const startTransforms = useRef<Record<GUID, Matrix4>>({});
  const accumulatedTransformChange = useRef<Matrix4>(new Matrix4());

  const initializeEdit = useCallback(
    (mode: InteractionMode) => {
      // Initialize state for new edit
      interactionMode.current = mode;
      const transformCache = selectDataPreparationTransformCache(getState());
      startTransforms.current = {};
      accumulatedTransformChange.current.identity();

      // Save the start position of all entities before the edit
      for (const id of affectedEntityIds) {
        const cachedTransform = transformCache[id];
        if (!cachedTransform) continue;
        startTransforms.current[id] = new Matrix4().fromArray(
          cachedTransform.worldMatrix,
        );
      }
    },
    [affectedEntityIds, getState],
  );

  const commitEdit = useCallback(() => {
    dispatch(commitInProgressTransformEdit());
  }, [dispatch]);

  const trackEditChanges = useCallback(
    (transformChange: Matrix4) => {
      // Moving the scans incurs many small changes to the scene,
      // use cheap rendering to maintain high frame rates
      invalidateSceneCheapEvent.emit();

      accumulatedTransformChange.current.premultiply(transformChange);

      const tempEntityMatrix = new Matrix4();
      const tempParentMatrix = new Matrix4();

      const transformOverrides: EntityTransformOverrides = {};

      for (const id of entitiesToEdit) {
        const entity = selectRevisionEntity(id)(getState());
        if (!entity) continue;

        // Apply the change to the entity
        const entityTransform = tempEntityMatrix
          .fromArray(
            selectRevisionEntityWorldTransformCache(id)(getState()).worldMatrix,
          )
          .premultiply(transformChange);

        const parentTransform =
          // A root element needs to be indicated by undefined to computeLocalEntityPoseFromWorldTransforms, not a default transform
          entity.parentId === null
            ? undefined
            : tempParentMatrix.fromArray(
                selectRevisionEntityWorldTransformCache(entity.parentId)(
                  getState(),
                ).worldMatrix,
              );

        // Convert back to local transform and capture tree coordinate system
        transformOverrides[id] = computeLocalEntityPoseFromWorldTransforms(
          entityTransform,
          parentTransform,
        );
      }

      const cachedWorldTransforms: RevisionTransformCache = {};
      const tempMatrix = new Matrix4();

      // Calculate the new world transforms for all entities being moved
      // Also includes indirectly moved entities (e.g. descendants)
      for (const id of affectedEntityIds) {
        const startTransform = startTransforms.current[id] ?? new Matrix4();
        const newTransform = tempMatrix.multiplyMatrices(
          accumulatedTransformChange.current,
          startTransform,
        );

        cachedWorldTransforms[id] = computeCachedWorldTransform(newTransform);
      }

      dispatch(
        updateInProgressTransformEdit({
          scanIds: entitiesToEdit,
          newPoses: transformOverrides,
          cachedWorldTransforms,
          mode: interactionMode.current,
        }),
      );
    },
    [entitiesToEdit, affectedEntityIds, getState, dispatch],
  );

  // If any selected scans are invisible, moving scans is disabled
  // Otherwise the user cannot see all changes they make
  const areSelectedScansVisible = useAppSelector(selectAreSelectedScansVisible);
  const isEditingEnabled =
    isEditingScans &&
    areSelectedScansVisible &&
    // Prevent accidental edits when changing the clipping box
    activeTool !== ToolName.clipScene;

  // Warn the user if they try to move scans while some are invisible
  useNonExhaustiveEffect(() => {
    if (isEditingScans && !areSelectedScansVisible) {
      openToast({
        variant: "warning",
        title: "Cannot move captures",
        message:
          "Moving is disabled while hidden captures are selected. Make them visible to enable editing.",
      });
    }
  }, [isEditingScans, areSelectedScansVisible]);

  const hasChanged = useChangeCache();

  // Sync back to initial state for live view (if the user toggles back)
  // Using useFrame with change detection instead of useEffect, because the effect cannot react to changes of the controls ref
  useFrame(() => {
    if (hasChanged("activeCamera", activeCamera)) {
      setInitialCamera(activeCamera);
    }
    if (hasChanged("controls", explorationControlsRef.current)) {
      setInitialOrbitControls(explorationControlsRef.current ?? undefined);
    }
    if (hasChanged("projection", activeProjection)) {
      setFromDraftSettings(activeProjection);
    }
  });

  if (animationToRender) {
    return animationToRender;
  }

  switch (activeProjection) {
    case Projection.twoDimensional:
      return (
        <EntityPinControls
          manipulatedEntityIds={isEditingEnabled ? entitiesToEdit : EMPTY_ARRAY}
          onTransform={trackEditChanges}
          onDragStart={initializeEdit}
          onDragEnd={commitEdit}
          mapControlsRef={map2dControlsRef}
          camera={orthoCamera2D}
        />
      );
    case Projection.threeDimensional:
      return (
        <ExplorationControls
          ref={explorationControlsRef}
          camera={
            active3DCamera === CameraViewType.orthographic
              ? orthoCamera3D
              : perspCamera
          }
          target={lastTarget}
          mouseBindings={MOUSE_BINDINGS_3D}
          // Prevent clicking a connection causing camera movement
          enableClickToFocus={!isAnyConnectionHovered}
        />
      );
  }
}
